import React, { createContext, useState, useEffect, useContext, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { API_URL } from '../config';
import {
  getStudentCourseTemario,
  getStudentCourseTestsStructure,
} from '../utils/studentCourseDataCache';
import { getCurrentUserProfile } from '../utils/profileCache';
import { normalizeCourseContenido, resolvePublishedCourseTabsAndContenido } from '../utils/normalizeCourseShape';

const StudentContext = createContext();

export { StudentContext };

export const useStudent = () => useContext(StudentContext);

function isActiveStudentCourse(course) {
  if (!course || course.activo === false) {
    return false;
  }
  if (!course.fechaCierre) {
    return true;
  }
  const closeDate = new Date(course.fechaCierre);
  return Number.isNaN(closeDate.getTime()) || closeDate >= new Date();
}

function getBlockName(block) {
  if (block?.nombre === null || block?.nombre === undefined) {
    return "";
  }
  return String(block.nombre);
}

function canStudentMessage(data) {
  const permission = data?.permisos?.puedeMensajear;
  return permission === true || permission === "true";
}

function chooseActiveCourse(prev, enrolledCourses, savedCourseId, belongsToUser) {
  const preferredId = prev?._id || (belongsToUser ? savedCourseId : null);
  const found = preferredId
    ? enrolledCourses.find((course) => course._id === preferredId)
    : null;

  if (enrolledCourses.length === 0) return null;
  if (!found) return enrolledCourses[0];
  if (!prev || prev._id !== found._id) return found;
  return { ...prev, ...found };
}

function syncSavedCourseId(enrolledCourses) {
  const savedCourseId = localStorage.getItem('activeCourseId');
  const belongsToUser = savedCourseId && enrolledCourses.some((course) => course._id === savedCourseId);
  if (savedCourseId && !belongsToUser) {
    localStorage.removeItem('activeCourseId');
  }
  return { savedCourseId, belongsToUser };
}

export const StudentProvider = ({ children }) => {
  const [courses, setCourses] = useState([]);
  const [activeCourse, setActiveCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [messagingEnabled, setMessagingEnabled] = useState(false);
  const [hasMessagingThreads, setHasMessagingThreads] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [activeCourseTabHints, setActiveCourseTabHints] = useState({
    contenidoTabs: [],
    testBlocks: [],
  });
  const [activeCourseNavigation, setActiveCourseNavigation] = useState({
    contenido: {},
    pestanasCurso: [],
    testEstructura: [],
  });
  const refreshControllerRef = useRef(null);

  const fetchHasMessagingThreads = useCallback(async (signal) => {
    try {
      const response = await fetch(`${API_URL}/mensajes/has-active-threads`, {
        credentials: "include",
        signal,
      });
      if (signal?.aborted) return;
      if (!response.ok) {
        setHasMessagingThreads(false);
        return;
      }
      const payload = await response.json();
      if (signal?.aborted) return;
      setHasMessagingThreads(!!payload?.hasActive);
    } catch (err) {
      if (err.name !== "AbortError") {
        setHasMessagingThreads(false);
      }
    }
  }, []);

  const updateMessagingState = useCallback(async (data, signal) => {
    const puedeMensajear = canStudentMessage(data);
    setMessagingEnabled(puedeMensajear);
    if (puedeMensajear) {
      setHasMessagingThreads(false);
      return;
    }
    await fetchHasMessagingThreads(signal);
  }, [fetchHasMessagingThreads]);

  const fetchStudentData = useCallback(async (signal, options = {}) => {
    const silent = options.silent === true;
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const data = await getCurrentUserProfile({ force: options.forceProfile === true });
      if (signal?.aborted) return;
      if (!data) {
        throw new Error('Error al cargar el perfil del estudiante');
      }

      setUserProfile(data);
      
      const enrolledCourses = (data.cursosInscritos || []).filter(isActiveStudentCourse);
      
      setCourses((prev) => {
        if (enrolledCourses.length > 0) return enrolledCourses;
        if (prev.length > 0) return prev;
        return enrolledCourses;
      });

      await updateMessagingState(data, signal);
      if (signal?.aborted) return;
      
      const { savedCourseId, belongsToUser } = syncSavedCourseId(enrolledCourses);

      setActiveCourse((prev) => chooseActiveCourse(prev, enrolledCourses, savedCourseId, belongsToUser));

    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error(err);
      if (!silent && !signal?.aborted) {
        setError(err.message);
      }
    } finally {
      if (!silent && !signal?.aborted) {
        setLoading(false);
      }
    }
  }, [updateMessagingState]);


  useEffect(() => {
    const controller = new AbortController();
    fetchStudentData(controller.signal);
    return () => {
      controller.abort();
      refreshControllerRef.current?.abort();
    };
  }, [fetchStudentData]);

  // Update localStorage when activeCourse changes
  useEffect(() => {
    if (activeCourse) {
      localStorage.setItem('activeCourseId', activeCourse._id);
    }
  }, [activeCourse]);

  useEffect(() => {
    if (!activeCourse?._id) {
      setActiveCourseTabHints({ contenidoTabs: [], testBlocks: [] });
      setActiveCourseNavigation({ contenido: {}, pestanasCurso: [], testEstructura: [] });
      return undefined;
    }

    const controller = new AbortController();
    const courseId = activeCourse._id;

    const loadTabHints = async () => {
      const [temarioResult, testsResult] = await Promise.allSettled([
        getStudentCourseTemario(courseId, { force: true }),
        getStudentCourseTestsStructure(courseId),
      ]);

      if (controller.signal.aborted) {
        return;
      }

      let contenidoTabs = [];
      let contenido = {};
      let pestanasCurso = [];
      if (temarioResult.status === "fulfilled" && !temarioResult.value?.error) {
        const temarioData = temarioResult.value;
        const contenidoPayload =
          temarioData?.contenido && typeof temarioData.contenido === "object"
            ? temarioData.contenido
            : {};
        contenido = normalizeCourseContenido(contenidoPayload);
        const published = resolvePublishedCourseTabsAndContenido(
          temarioData?.pestanasCurso,
          contenido,
        );
        pestanasCurso = published.pestanasCurso;
        contenido = published.contenido;
        contenidoTabs = published.pestanasCurso;
      }

      let testBlocks = [];
      let testEstructura = [];
      if (testsResult.status === "fulfilled") {
        const testsData = testsResult.value;
        testEstructura = Array.isArray(testsData?.estructura) ? testsData.estructura : [];
        testBlocks = testEstructura
          .map(getBlockName)
          .filter((name) => name.length > 0);
      }

      setActiveCourseTabHints({ contenidoTabs, testBlocks });
      setActiveCourseNavigation({ contenido, pestanasCurso, testEstructura });
    };

    loadTabHints();
    return () => {
      controller.abort();
    };
  }, [activeCourse?._id]);

  // Admin/Debug override: Server-mediated
  const requestMessagingOverride = useCallback(async (enable) => {
    try {
      const response = await fetch(`${API_URL}/usuarios/toggle-messaging`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ habilitar: enable }),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to toggle messaging permission');
      }

      const data = await response.json();
      
      // Update local state directly if successful
      setMessagingEnabled(!!data.puedeMensajear);
      
      return true;
    } catch (err) {
      console.error("Messaging override failed:", err);
      return false;
    }
  }, []);

  const refreshCourses = useCallback(
    () => {
      refreshControllerRef.current?.abort();
      const controller = new AbortController();
      refreshControllerRef.current = controller;
      return fetchStudentData(controller.signal, { forceProfile: true, silent: true }).finally(() => {
        if (refreshControllerRef.current === controller) {
          refreshControllerRef.current = null;
        }
      });
    },
    [fetchStudentData],
  );

  const value = useMemo(() => ({
    userProfile, // Expose the full profile for student-only views.
    courses,
    activeCourse,
    setActiveCourse,
    loading,
    error,
    refreshCourses,
    messagingEnabled,
    hasMessagingThreads,
    showMessagingNav: messagingEnabled,
    requestMessagingOverride,
    activeCourseTabHints,
    activeCourseNavigation,
  }), [
    userProfile,
    courses,
    activeCourse,
    loading,
    error,
    refreshCourses,
    messagingEnabled,
    hasMessagingThreads,
    requestMessagingOverride,
    activeCourseTabHints,
    activeCourseNavigation,
  ]);

  return (
    <StudentContext.Provider value={value}>
      {children}
    </StudentContext.Provider>
  );
};

StudentProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
